import React from 'react';
import { Divider, Link, ListItem, ListItemText, styled } from '@mui/material';
import LaunchIcon from '@mui/icons-material/Launch';
import { useTranslationContext } from 'contexts/TranslationContext/TranslationContext';
import { Button } from 'components/Button/Button';
import {
  SUPPORT_EMAIL_ADDRESS,
  useSupportMailtoLink,
} from 'features/settings/hooks/useInternalUserMetaData';

const StyledVisitHelpCenterButton = styled(Button)(({ theme }) => ({
  marginLeft: theme.spacing(2),
  marginTop: theme.spacing(1),
}));

const StyledDivider = styled(Divider)(({ theme }) => ({
  marginTop: theme.spacing(3),
  marginBottom: theme.spacing(3),
}));

const StyledListItem = styled(ListItem)(({ theme }) => ({
  [theme.breakpoints.down('md')]: {
    width: '100%',
  },
  [theme.breakpoints.between('md', 'lg')]: {
    width: '75%',
  },
  [theme.breakpoints.up('lg')]: {
    width: '50%',
  },
}));

const StyledLaunchIcon = styled(LaunchIcon)(({ theme }) => ({
  marginLeft: theme.spacing(1),
}));

const SubmitFeedbackSecondaryText = () => {
  const emailAddress = SUPPORT_EMAIL_ADDRESS;
  const mailtoLink = useSupportMailtoLink();

  const { translation } = useTranslationContext();

  const feedbackText = translation('opinion_matters_email_feedback', {
    arg1: emailAddress,
  }).split(emailAddress);

  return (
    <>
      {feedbackText[0]}
      <Link
        href={mailtoLink}
        rel="noopener noreferrer"
        aria-label={`Send email to ${emailAddress}`}
        id="support-email-link"
      >
        {emailAddress}
      </Link>
      {feedbackText[1]}
    </>
  );
};

export const SupportAndFeedbackDesktopContent = () => {
  const { translation } = useTranslationContext();

  const handleHelpCenterClick = () => {
    window.open('https://help.wellframe.com', '_blank', 'noopener,noreferrer');
  };

  return (
    <>
      <StyledListItem className="support-and-feedback__help-center-item">
        <ListItemText
          primary={translation('help_center')}
          secondary={translation('help_center_faq_subtext')}
          primaryTypographyProps={{
            color: 'grey.700',
            textTransform: 'capitalize',
          }}
          secondaryTypographyProps={{
            color: 'grey.600',
          }}
        />
      </StyledListItem>
      <StyledVisitHelpCenterButton
        variant="primary"
        size="large"
        onClick={handleHelpCenterClick}
        id="visit-help-center-button"
        aria-label="Visit Help Center in a new tab"
        data-testid="visit-help-center-button"
      >
        {translation('visit_help_center')}
        <StyledLaunchIcon fontSize="small" />
      </StyledVisitHelpCenterButton>
      <StyledDivider />
      <StyledListItem className="support-and-feedback__submit-feedback-item">
        <ListItemText
          primary={translation('submit_feedback')}
          secondary={<SubmitFeedbackSecondaryText />}
          primaryTypographyProps={{
            color: 'grey.700',
            textTransform: 'capitalize',
          }}
          secondaryTypographyProps={{
            color: 'grey.600',
          }}
        />
      </StyledListItem>
    </>
  );
};
